import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslationService } from '@spartacus/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ShipmentsService } from './core/shipments.service';
import { ShipmentsStatusRequest, ShipmentsRequestConfig } from '../../../core/models/shipments.model';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { initialStatus, loadingStatus } from '../../../core/models/request-status.model';
import { Option } from '../../../core/models/option.model';
import { CustomValidatorService } from '../../../components/form-elements/custom-validator.service';
import { TableProConfig } from '../../../components/table-pro/core/table-pro.model';
import { ExcelDownloadService } from '../../../core/services/excelDownload.service';

@Component({
  selector: 'app-shipments',
  templateUrl: './shipments.component.html',
  styleUrls: ['./shipments.component.scss'],
  standalone: false,
})
export class ShipmentsComponent implements OnInit, OnDestroy {
  form: FormGroup;
  shipmentsRequest$ = new BehaviorSubject<ShipmentsStatusRequest>({
    ...initialStatus,
  });
  options$ = new BehaviorSubject<Option[]>([]);

  minDate = new Date(new Date().setDate(new Date().getDate() - 365));
  maxDate = new Date();
  subscription = new Subscription();

  tableConfig: TableProConfig = {
    collumnConfig: [
      { id: 'order', index: 0, type: 'number', isOrdenable: true },
      { id: 'orderDate', index: 1, type: 'date', isOrdenable: true },
      { id: 'expShipDate', index: 2, type: 'date', isOrdenable: true },
      { id: 'actualShipDate', index: 3, type: 'date', isOrdenable: true },
      { id: 'poNumber', index: 4, type: 'number', isOrdenable: true },
      { id: 'trackingNumber', index: 5, type: 'text', isOrdenable: true },
      { id: 'invoiceNumber', index: 6, type: 'text', isOrdenable: true },
      { id: 'partNumber', index: 7, type: 'text', isOrdenable: true },
      { id: 'quantityOrdered', index: 8, type: 'number', isOrdenable: true },
    ],
    currentPage: 0,
    itemsPerPage: 5,
    itemsPerPageConfig: [5, 10, 15, 20, 50],
    hasItemsPerPageSelector: true,
    hasSearchBox: true,
    inputSearch: {
      placeholder: 'Search...',
    },
    noContentMessage: 'No shipments available.',
  };
  constructor(
    private fb: FormBuilder,
    private shipmentsService: ShipmentsService,
    private customValidatorService: CustomValidatorService,
    private excelDownloadService: ExcelDownloadService,
    private translationService: TranslationService,
  ) {
    this.form = this.fb.group({
      searchBy: ['orderDate'],
      searchValue: [null],
      searchType: ['90'],
      fromDate: [new Date(new Date().setDate(new Date().getDate() - 90))],
      toDate: [this.maxDate],
      openOnly: [false, []],
      delayedOnly: [false, []],
      poNumber: [null],
      invoiceNumber: [null],
      partNumber: [null],
      orderNumber: [null],
    });
  }

  ngOnInit(): void {
    this.listenToSearchBy();
    this.listenToSearchType();
    this.loadOptions();
    this.submit();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  listenToSearchType(): void {
    this.subscription.add(
      this.form.get('searchType')?.valueChanges.subscribe(value => {
        if (value === 'custom') {
          this.evaluateValidation(value as string);
        } else {
          this.setDaysToSearchByQuantity(value as string);
        }
      }),
    );
  }

  listenToSearchBy(): void {
    this.subscription.add(
      this.form.get('searchBy')?.valueChanges.subscribe(searchBy => {
        this.resetFormFields();

        if (searchBy === 'orderDate') {
          this.form.get('searchType')?.setValue('90');
          this.setDaysToSearchByQuantity('90');
        } else {
          this.form.get('fromDate')?.setValue(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
          this.form.get('toDate')?.setValue(this.maxDate);
          this.form.get('openOnly')?.setValue(false);
          this.form.get('delayedOnly')?.setValue(false);
        }
      }),
    );
  }

  loadOptions(): void {
    this.subscription.add(
      combineLatest([
        this.translationService.translate('formSearch.dateRangeOptions.last30Days'),
        this.translationService.translate('formSearch.dateRangeOptions.last60Days'),
        this.translationService.translate('formSearch.dateRangeOptions.last90Days'),
        this.translationService.translate('formSearch.dateRangeOptions.customRange'),
      ]).subscribe(([last30Days, last60Days, last90Days, customRange]) => {
        this.options$.next([
          { code: '30', label: last30Days },
          { code: '60', label: last60Days },
          { code: '90', label: last90Days },
          { code: 'custom', label: customRange },
        ]);
      }),
    );
  }

  resetFormFields(): void {
    const fields = ['poNumber', 'invoiceNumber', 'partNumber', 'orderNumber'];
    fields.forEach(field => this.form.get(field)?.setValue(null));
    this.form.get('searchValue')?.setValue(null);
  }

  evaluateValidation(searchType: string): void {
    const removeValidations = (control: string) => {
      this.form.get(control)?.clearValidators();
      this.form.get(control)?.updateValueAndValidity();
    };

    const addValidations = (control: string) => {
      this.form.get(control)?.setValidators([this.customValidatorService.required()]);
      this.form.get(control)?.updateValueAndValidity();
    };

    const fields = ['fromDate', 'toDate'];

    fields.forEach(field => removeValidations(field));

    if (searchType === 'custom') {
      fields.forEach(field => addValidations(field));
    }
  }

  setDaysToSearchByQuantity(days: string): void {
    const qtyDays = parseInt(days, 10);

    this.form.get('fromDate')?.setValue(new Date(new Date().setDate(new Date().getDate() - qtyDays)));
    this.form.get('toDate')?.setValue(this.maxDate);
  }

  backSearchType(): void {
    this.form.get('searchType')?.setValue('90');
  }

  submit(): void {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      const searchBy = this.form.get('searchBy')?.value;
      const query = this.buildQuery(searchBy);
      this.fetchShipments(query);
    }
  }

  onEnter(event: Event) {
    event.preventDefault();
    this.submit();
  }

  buildQuery(searchBy: string) {
    const { fromDate, toDate, searchValue } = this.form.value;

    const formattedFromDate = new Date(fromDate).toISOString().split('T')[0];
    const formattedToDate = new Date(toDate).toISOString().split('T')[0];

    if (searchBy === 'orderDate') {
      return {
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        openOnly: this.form.get('openOnly')?.value || false,
        delayedOnly: this.form.get('delayedOnly')?.value || false,
      };
    } else {
      return {
        fromDate: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0],
        toDate: new Date().toISOString().split('T')[0],
        openOnly: this.form.get('openOnly')?.value || false,
        delayedOnly: this.form.get('delayedOnly')?.value || false,
        poNumber: searchBy === 'poNumber' ? searchValue : null,
        invoiceNumber: searchBy === 'invoiceNumber' ? searchValue : null,
        partNumber: searchBy === 'partNumber' ? searchValue : null,
        orderNumber: searchBy === 'orderNumber' ? searchValue : null,
      };
    }
  }

  fetchShipments(query: ShipmentsRequestConfig): void {
    this.shipmentsRequest$.next({ ...loadingStatus });

    this.subscription.add(
      this.shipmentsService.getShipments(query).subscribe(response => {
        this.shipmentsRequest$.next(response);
      }),
    );
  }

  exportShipments(): void {
    if (this.form.valid) {
      const searchBy = this.form.get('searchBy')?.value;
      const query = this.buildQuery(searchBy);
      this.subscription.add(
        this.shipmentsService.exportShipments(query).subscribe(response => {
          if (response && response.fileContentBase64) {
            this.excelDownloadService.downloadBase64File(response);
          }
        }),
      );
    }
  }

  clearAll(): void {
    window.location.reload();
  }
}
